import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

export default function PoojasPage() {
  const [poojas, setPoojas] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get("/poojas").then(({ data }) => setPoojas(data));
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditing(p.slug);
    setForm({ ...p, benefits_text: (p.benefits || []).join("\n") });
  };

  const save = async () => {
    const payload = {
      name: form.name,
      short_description: form.short_description,
      introduction: form.introduction,
      significance: form.significance,
      scriptural_importance: form.scriptural_importance,
      who_should_perform: form.who_should_perform,
      benefits: (form.benefits_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
      procedure: form.procedure,
      duration: form.duration,
      required_preparation: form.required_preparation,
      price: Number(form.price) || 0,
      deposit_amount: Number(form.deposit_amount) || 0,
      booking_enabled: !!form.booking_enabled,
      featured: !!form.featured,
      image_url: form.image_url,
    };
    await api.patch(`/poojas/${editing}`, payload);
    toast.success("Pooja updated");
    setEditing(null);
    load();
  };

  return (
    <div data-testid="admin-poojas-page">
      <h1 className="font-heading text-3xl">Poojas</h1>
      <div className="mt-5 bg-white border border-[color:var(--border)] rounded-md divide-y divide-[color:var(--border)]">
        {poojas.map((p) => (
          <div key={p.slug} className="p-4 flex items-center gap-4" data-testid={`pooja-admin-row-${p.slug}`}>
            <img src={p.image_url} alt={p.name} className="w-16 h-16 rounded-md object-cover border border-[color:var(--border)]" />
            <div className="flex-1">
              <div className="font-heading text-lg">{p.name}</div>
              <div className="text-xs opacity-65">₹{Number(p.price).toLocaleString("en-IN")} · {p.booking_enabled ? "Bookings ON" : "Bookings OFF"} · Cutoff: {p.booking_cutoff_days ?? 5}d · {p.featured ? "Featured" : "Standard"}</div>
            </div>
            <button onClick={() => openEdit(p)} className="px-3 py-1.5 text-sm rounded-md border border-[color:var(--border)]" data-testid={`edit-pooja-${p.slug}`}>Edit</button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md p-6 w-full max-w-2xl my-10" data-testid="pooja-edit-modal">
            <h3 className="font-heading text-2xl">Edit Pooja</h3>
            <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm">
              <Input placeholder="Name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" />
              <Input placeholder="Image URL" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="edit-image" />
              <Input placeholder="Price (INR)" type="number" value={form.price || 0} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="edit-price" />
              <Input placeholder="Deposit Amount (INR)" type="number" value={form.deposit_amount || 0} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} data-testid="edit-deposit" />
              <Input placeholder="Duration" value={form.duration || ""} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="edit-duration" />
              <div>
                <label className="text-xs opacity-70 block mb-1">Booking Cutoff (days in advance)</label>
                <Input
                  type="number"
                  min={0}
                  value={form.booking_cutoff_days ?? 5}
                  onChange={(e) => setForm({ ...form, booking_cutoff_days: e.target.value })}
                  data-testid="edit-cutoff-days"
                />
                <p className="text-[11px] opacity-60 mt-1">Devotees must book this pooja at least this many days before the pooja date. Default: 5.</p>
              </div>
              <div className="flex items-center gap-3 px-1">
                <Switch checked={!!form.booking_enabled} onCheckedChange={(v) => setForm({ ...form, booking_enabled: v })} data-testid="edit-booking-enabled" />
                <span className="text-sm">Bookings Enabled</span>
                <Switch checked={!!form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="edit-featured" />
                <span className="text-sm">Featured</span>
              </div>
              <div className="md:col-span-2">
                <Textarea placeholder="Short Description" value={form.short_description || ""} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="edit-short" />
              </div>
              <div className="md:col-span-2">
                <Textarea rows={4} placeholder="Introduction" value={form.introduction || ""} onChange={(e) => setForm({ ...form, introduction: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Textarea rows={4} placeholder="Significance" value={form.significance || ""} onChange={(e) => setForm({ ...form, significance: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Textarea rows={3} placeholder="Benefits (one per line)" value={form.benefits_text || ""} onChange={(e) => setForm({ ...form, benefits_text: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Textarea rows={4} placeholder="Procedure" value={form.procedure || ""} onChange={(e) => setForm({ ...form, procedure: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Textarea rows={3} placeholder="Required Preparation" value={form.required_preparation || ""} onChange={(e) => setForm({ ...form, required_preparation: e.target.value })} />
              </div>
            </div>
            <div className="mt-5 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border)]" data-testid="edit-cancel">Cancel</button>
              <button onClick={save} className="px-5 py-2 rounded-full btn-brand text-sm" data-testid="edit-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
